import { Modal } from '@pmi/dsm-react';
import { ReactNode, useState } from 'react';
import { BaseInternalAdmin } from '../models';
import {
  useRemoveInternalAdminInviteMutation,
  useRemoveInternalAdminMutation,
} from './apiSlice';
import { toastWithAnalytics } from '../../../../../shared/analytics';
import { AdministratorUserApiModel } from '../../AdminsTab/models';

interface RemoveInternalAdminModalProps {
  admin: BaseInternalAdmin;
  onCancel?: () => void;
}

export default function RemoveInternalAdminModal({
  admin,
  onCancel = () => {},
}: RemoveInternalAdminModalProps) {
  const [removeAdmin, removedAdmin] = useRemoveInternalAdminMutation();
  const [removeAdminInvite, removedAdminInvite] =
    useRemoveInternalAdminInviteMutation();
  const [isProcessing, setIsProcessing] = useState(false);
  const onRemove = () => {
    setIsProcessing(true);
    let removeAdminPromise: Promise<AdministratorUserApiModel>;
    if (admin.personId) {
      removeAdminPromise = removeAdmin({
        id: admin.id,
      }).unwrap();
    } else if (admin.inviteCode) {
      removeAdminPromise = removeAdminInvite({
        inviteId: admin.inviteCode,
      }).unwrap();
    }

    if (removeAdminPromise) {
      removeAdminPromise
        .then(() => {
          const successMessage = (): ReactNode => (
            <>
              You have removed{' '}
              <span data-exclude-lg>{admin.invitationEmail}</span>
            </>
          );
          toastWithAnalytics(successMessage(), 'success');
          onCancel();
          removedAdmin.reset();
          removedAdminInvite.reset();
        })
        .catch((err) => {
          const errorMessage = (): ReactNode => (
            <>
              User could not be removed.{' '}
              <span data-exclude-lg>{err.data.detail}</span>
            </>
          );
          toastWithAnalytics(errorMessage(), 'danger');
          setIsProcessing(false);
        })
        .finally(() => setIsProcessing(false));
    }
  };
  const onCancelHandler = () => {
    onCancel();
  };

  if (!admin) return null;

  return (
    <Modal
      show
      onClose={onCancelHandler}
      headline="Remove?"
      description="This will remove their administrative access but they may continue to access any agreements they have been added to."
      headline_description={`Are you sure you want to remove ${admin.invitationEmail}?`}
      customClassName="removeInternalAdmin"
      buttons={[
        {
          titleText: 'Cancel',
          variant: 'outline-secondary',
          onClick: onCancelHandler,
          disabled: isProcessing,
        },
        {
          titleText: 'Yes, Remove',
          variant: 'primary',
          onClick: onRemove,
          disabled: isProcessing,
        },
      ]}
    />
  );
}
